import "../../common/styles/Elements.scss";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem, Select, TextField } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import Search from '@material-ui/icons/Search';
import { ApiErrors } from "common/backend/api-errors/errors";
import { Investment } from 'common/backend/generated/investment-model';
import { User } from "common/backend/generated/user-model";
import { IdNowStatus, IdNowUserFieldStatus } from "common/backend/types/utils";
import { CommonToast } from 'common/components';
import { CommonErrorDialog } from 'common/components/CommonErrorDialog';
import { UserField, UserInputTypeKey } from "common/components/input/fields";
import { ApproveUser, GetIdNowUser, GetInvestments, GetUsers } from 'common/graphql/queryDescriptors';
import { useCustomQuery } from 'common/store/hooks/useCustomQuery';
import { GetInvestmentManageable, InvestmentManageable } from 'common/store/investment/types';
import { ApproveUserManageable, GetIdNowUserManageable, UserManageable } from 'common/store/user/types';
import { errorMessages } from 'common/utils/validation';
import Navbar from 'components/Navbar';
import { Form, Formik, FormikErrors } from 'formik';
import React, { useMemo, useState } from 'react';

const ApprovalView = ({
  user,
  onApproveSuccessfull,
  ...props
}: any) => {
  // State
  const [idNowInfo, setIdNowInfo] = useState({});
  const [error, setError] = useState("");

  // Hooks
  useCustomQuery(GetIdNowUser({
    input: { input: new GetIdNowUserManageable({ idNowTransactionId: user.idNowTransactionId }).serialize() },
    onSuccess: (data: any) => {
      setIdNowInfo(new ApproveUserManageable(data));
    }
  }));

  let { execute: approveUser } = useCustomQuery(ApproveUser({
    onError: (error: ApiErrors) => {
      if (error === ApiErrors.USER_APPROVAL_NOT_NEEDED) {
        setError("Approval not needed");
      }
    },
    onSuccess: () => {
      onApproveSuccessfull();
    },
  }));

  // Validation
  const validate = (values: ApproveUserManageable): FormikErrors<any> => {
    return new ApproveUserManageable(values).validate(
      Object.keys(values),
      errorMessages
    );
  }

  const onSubmit = async (
    values: ApproveUserManageable, { setSubmitting }: any
  ) => {
    setSubmitting(true);

    await approveUser({ variables: { input: new ApproveUserManageable(values).serialize() } });

    props.onClose();

    setSubmitting(false);
  }

  // Options
  const ignoreKeys = [
    'id',
  ];

  // Helpers
  const capitalize = (sentence: string) => {
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }

  const keyToLabel = (sentence: string) : string => {
    const matches = sentence.match(/[A-Z][a-z]+/g)
    if (!matches) {
      return ''
    }
    return matches.map((word: string) => capitalize(word.toLowerCase())).join(' ');
  }

  return (
    <Formik
      initialValues={user}
      validate={validate}
      onSubmit={onSubmit}
    >
      {(formProps) => {
        const {
          submitForm,
          values,
        } = formProps;

        <Form style={{ width: "100%" }}>
          <Dialog {...props}>
            <DialogTitle id="customized-dialog-title" color="textSecondary">
              <Typography variant="h2" color="textSecondary">
                Review user info
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell className="bold">Name</TableCell>
                    <TableCell className="bold">Current value</TableCell>
                    <TableCell className="bold">Status</TableCell>
                    <TableCell className="bold">Changed value</TableCell>
                  </TableRow>
                </TableHead>

                {idNowInfo &&
                Object.keys(idNowInfo)
                  .filter((key: string) => !ignoreKeys.includes(key))
                  .map((
                    key: string, i: number
                  ) => {

                    const item = idNowInfo[key];

                    let isChanged =
                      typeof item === 'string' ? item.toUpperCase() !== values[key].toUpperCase() :
                        item !== values[key];

                    return (
                      <TableBody key={i}>
                        <TableRow>
                          <TableCell>{keyToLabel(capitalize(key))}</TableCell>
                          <TableCell>
                            <UserField
                              {...formProps}
                              id={key as UserInputTypeKey}
                              disabled={!isChanged}
                              noLabels
                            />
                          </TableCell>
                          <TableCell>{isChanged ? IdNowUserFieldStatus.CHANGED.toUpperCase() : IdNowUserFieldStatus.MATCH.toUpperCase()}</TableCell>
                          <TableCell>
                            {isChanged ?
                              <UserField
                                {...formProps}
                                id={key as UserInputTypeKey}
                                values={idNowInfo}
                                disabled={true}
                                noLabels
                              />
                              :
                              <></>
                            }</TableCell>
                        </TableRow>
                      </TableBody>
                    )
                  })}
              </Table>
            </DialogContent>

            <DialogActions>
              <Button autoFocus onClick={submitForm} color="primary" variant="contained">
                Approve
              </Button>
              <Button autoFocus onClick={props.onClose} color="primary" variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <CommonErrorDialog error={error} setError={setError} />
        </Form>
      }}
    </Formik>
  );
}

function Row(props: { row: any, onUserApproved: any }) {
  // State
  const [data, setData] = React.useState([]);

  const [open, setOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openApproval, setOpenApproval] = React.useState(false);

  // Options
  const {
    row,
    onUserApproved,
  } = props;

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleCloseApproval = () => {
    setOpenApproval(false);
  }

  const handleClose = () => {
    setOpenDialog(false);
  };

  // Actions
  const { execute: getInvestments } = useCustomQuery(GetInvestments({
    onSuccess: (data: any) => {
      setData(data ? data.map((investment: Investment) => new InvestmentManageable(investment)) : []);
      handleClickOpen();
    },
    skip: true,
  }));

  return (
    <React.Fragment>
      <TableRow >
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell component="th" scope="row">{row.firstName + ' ' + row.lastName}</TableCell>

        <TableCell align="right">{row.email}</TableCell>

        <TableCell align="right">{row.addressCountry}</TableCell>

        <TableCell align="right" className="capital">{row.whitelisted === true ? 'Yes' : 'No'}</TableCell>

        {row.idNowStatus === IdNowStatus.NEEDS_APPROVAL &&
        <TableCell align="right" >
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenApproval(true)}
          >Review</Button>
        </TableCell>
        ||
        <TableCell align="right" >
          <Button
            variant="contained"
            color="primary"
            onClick={() => getInvestments({ variables: { input: new GetInvestmentManageable({ userId: row.id }).serialize() } })}
          >investments</Button>
        </TableCell>
        }
      </TableRow>

      {row.idNowStatus === IdNowStatus.NEEDS_APPROVAL && openApproval &&
      <ApprovalView
        user={row}
        fullScreen
        onClose={handleCloseApproval}
        aria-labelledby="customized-dialog-title"
        open={openApproval}
        onApproveSuccessfull={() => onUserApproved()}
      />
      }

      <Dialog fullScreen onClose={handleClose} aria-labelledby="customized-dialog-title" open={openDialog}>
        <DialogTitle id="customized-dialog-title" color="textSecondary">
          <Typography variant="h2" color="textSecondary">
            Investments
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Table size="small" aria-label="purchases">
            <TableHead>
              <TableRow>
                <TableCell className="bold">Sale</TableCell>
                <TableCell className="bold">Amount (€)</TableCell>
                <TableCell className="bold">Payment method</TableCell>
                <TableCell className="bold" align="right" >Accont holder name</TableCell>
                <TableCell className="bold" align="right" >IBAN</TableCell>
              </TableRow>
            </TableHead>

            {data && data.map((item: any) => {
              return (
                <TableBody>
                  <TableRow key={''/* row.payment_accounts[0].bank*/}>
                    <TableCell>{/* item.sale.name*/}</TableCell>
                    <TableCell>{item.amountShares}</TableCell>
                    <TableCell>{/* row.payment_accounts[0].bank*/}</TableCell>
                    <TableCell align="right" >
                      {/* row.payment_accounts[0].name*/}
                    </TableCell>
                    <TableCell align="right" >{/* row.payment_accounts[0].IBAN*/}</TableCell>
                  </TableRow>
                </TableBody>
              )
            })}
          </Table>
        </DialogContent>

        <DialogActions>
          <Button autoFocus onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <TableRow className="row">
        <TableCell className="tight" colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div" color="textSecondary">
                User detailed information
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell className="bold narrow">Birth date</TableCell>
                    <TableCell align="right" className="bold narrow">Address</TableCell>
                    <TableCell align="right" className="bold narrow">Address zip</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow key={row.id}>
                    <TableCell>
                      {String(row.birthDate).slice(
                        0,
                        10
                      )}
                    </TableCell>
                    <TableCell align="right" >{row.address + ', ' + row.addressCity}</TableCell>
                    <TableCell align="right" >{row.addressZipcode}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <TableRow className="row">
        <TableCell className="tight" colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div" color="textSecondary">
                User passport information
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell className="bold narrow">Passport number</TableCell>
                    <TableCell align="right" className="bold narrow">Passport country</TableCell>
                    <TableCell align="right" className="bold narrow">Passport issued date</TableCell>
                    <TableCell align="right" className="bold narrow">Passport expiry date</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow key={row.passportNumber}>
                    <TableCell>{row.passportNumber}</TableCell>
                    <TableCell align="right" >
                      {row.passportIssuingCountry}
                    </TableCell>
                    <TableCell align="right" >{String(row.passportIssuedDate).slice(
                      0,
                      10
                    )}</TableCell>
                    <TableCell align="right" >{String(row.passportExpiryDate).slice(
                      0,
                      10
                    )}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <TableRow className="row">
        <TableCell className="tight" colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div" color="textSecondary">
                User experience information
              </Typography>

              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell className="bold narrow">Current occupation</TableCell>
                    <TableCell align="right" className="bold narrow">Previous occupation</TableCell>
                    <TableCell align="right" className="bold narrow">Highest level of education</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow key={row.employment}>
                    <TableCell>{row.employment}</TableCell>
                    <TableCell align="right" >
                      {row.prevEmployment}
                    </TableCell>
                    <TableCell align="right" >{row.highestEducation}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export const Users = () => {
  // State
  const [currentStatus, setStatus] = useState("ALL");
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentName, setCurrentName] = useState('');
  const [data, setData] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);

  useCustomQuery(GetUsers({
    onSuccess: (data: any) => {
      setData(data.map((user: User) => new UserManageable(user)));
    },
  }));

  // Options
  let rows = useMemo(
    () => {
      return data ?
        data.filter((
          elem: any, index: number, self: any
        ) => self.findIndex((t: any) => {
          return t.id === elem.id && t.y === elem.y
        }) === index) :
        [];
    },
    [data]
  );

  // Actions
  let emailFilteredRows = useMemo(
    () => rows.filter((item: any) => item.email.includes(currentEmail)),
    [rows, currentEmail]
  );

  let nameFilteredRows = useMemo(
    () => emailFilteredRows.filter((item: any) => String(item.firstName).toLowerCase().includes(currentName.toLowerCase()) || String(item.lastName).toLowerCase().includes(currentName.toLowerCase())),
    [emailFilteredRows, currentName]
  );

  let filteredRows = useMemo(
    () => nameFilteredRows.filter((item: any) => currentStatus === "ALL" || String(item.idNowStatus) === currentStatus),
    [nameFilteredRows, currentStatus]
  );

  return (
    <>
      <Navbar />
      <Grid container direction="row" justify="flex-start">
        <Grid item lg={1}></Grid>
        <Grid item lg={6} className="title">
          <Typography variant="h2">
            Users information
          </Typography>
        </Grid>
        <Grid item lg={1}></Grid>
      </Grid>

      <Grid container direction="row" justify="flex-end" spacing={1}>
        <Grid item lg={5}></Grid>

        <Grid item lg={2} className="right">
          <TextField
            id="standard-search"
            variant="outlined"
            label="Search by email"
            type="search"
            value={currentEmail}
            onChange={(value: any) => setCurrentEmail(value.target.value)}
            InputProps={{
              startAdornment:
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ,
            }}
          />
        </Grid>

        <Grid item lg={2} className="right" >
          <TextField
            id="standard-search"
            variant="outlined"
            label="Search by name"
            type="search"
            value={currentName}
            onChange={(value: any) => setCurrentName(value.target.value)}
            InputProps={{
              startAdornment:
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ,
            }}
          />
        </Grid>

        <Grid item lg={2} className="right">
          <Select
            fullWidth
            value={currentStatus}
            labelId="demo-simple-select-label"
            id="demo-simple-select"
          >
            <MenuItem value={"ALL"} onClick={() => setStatus("ALL")}>
              All
            </MenuItem>

            <MenuItem value={IdNowStatus.NEEDS_APPROVAL} onClick={() => setStatus(IdNowStatus.NEEDS_APPROVAL)}>
              Pending approval
            </MenuItem>
          </Select>
        </Grid>

        <Grid item lg={1}></Grid>
      </Grid>
      <br />

      <Grid container direction="row" justify="center">
        <Grid item lg={12}>
        </Grid>
        <Grid item lg={10}>

          <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell className="bold">Name</TableCell>
                  <TableCell align="right" className="bold">Email</TableCell>
                  <TableCell align="right" className="bold">Address Country</TableCell>
                  <TableCell align="right" className="bold">Whitelisted</TableCell>
                  {/* <TableCell align="right">Join Date</TableCell> */}
                  <TableCell />
                  {/* <TableCell /> */}

                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((
                  row: any, index: number
                ) =>
                  <Row
                    key={index}
                    row={row}
                    onUserApproved={() => setToastOpen(true)}
                  />)}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <CommonToast
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        text={"Changes saved!"}
        severity={"success"}
      />
    </>
  );
}